import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { parseLocalePath, buildLocaleUrl, LOCALES } from "@/i18n/config";

/**
 * SEO helper: manages <title>, meta description, canonical, OG/Twitter,
 * JSON-LD schema, and hreflang alternates for all supported locales.
 * The `path` prop should be the English (default-locale) path — hreflang
 * alternates are computed from it.
 */
export default function SEO({ title, description, path = "/", schema, image }) {
    const { pathname } = useLocation();

    useEffect(() => {
        if (title) document.title = title;

        // Detect current locale from the URL (English if no prefix)
        const { locale: currentLocale } = parseLocalePath(pathname);

        const upsertMeta = (attr, key, value) => {
            if (value == null) return;
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
        };

        const upsertLink = (rel, hreflang, href) => {
            const selector = hreflang
                ? `link[rel="${rel}"][hreflang="${hreflang}"]`
                : `link[rel="${rel}"]`;
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("link");
                el.setAttribute("rel", rel);
                if (hreflang) el.setAttribute("hreflang", hreflang);
                document.head.appendChild(el);
            }
            el.setAttribute("href", href);
        };

        const origin = "https://exportfy.world";
        const localizedPath = buildLocaleUrl(path, currentLocale);
        const canonical = `${origin}${localizedPath}`;

        upsertMeta("name", "description", description);
        upsertMeta("property", "og:title", title);
        upsertMeta("property", "og:description", description);
        upsertMeta("property", "og:type", "website");
        upsertMeta("property", "og:url", canonical);
        upsertMeta("property", "og:locale", currentLocale);
        if (image) upsertMeta("property", "og:image", image);
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", title);
        upsertMeta("name", "twitter:description", description);

        // Canonical (for the current-locale URL)
        upsertLink("canonical", null, canonical);

        // Remove stale hreflang links first
        document.head
            .querySelectorAll('link[rel="alternate"][hreflang]')
            .forEach((el) => el.remove());
        // Emit hreflang alternates for every supported locale
        LOCALES.forEach(({ code }) => {
            const href = `${origin}${buildLocaleUrl(path, code)}`;
            upsertLink("alternate", code, href);
        });
        // x-default -> English canonical
        upsertLink("alternate", "x-default", `${origin}${buildLocaleUrl(path, "en")}`);

        // JSON-LD (supports array or single object)
        const scriptId = "page-schema-jsonld";
        let s = document.getElementById(scriptId);
        if (schema) {
            if (!s) {
                s = document.createElement("script");
                s.type = "application/ld+json";
                s.id = scriptId;
                document.head.appendChild(s);
            }
            s.textContent = JSON.stringify(schema);
        } else if (s) {
            s.remove();
        }
    }, [title, description, path, schema, image, pathname]);

    return null;
}
